import { useState, useEffect, useRef } from 'react';
import { AxiosRequestConfig } from 'axios';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'redux/hooks';
import { postDealCalc, PostDealCalcRequestType } from 'service/apis/CreateDealapi';
import { toast } from 'sonner';
import {
  setTcs,
  setServicePartnerId,
  setForeignBankRates,
  setTTRates,
  setServicePartnerMode,
  setSettlementRates,
  setcal_total_charges,
  setcal_profit,
  setcal_amount_inr,
  setcal_gst,
  setcal_tt_charges,
  setcal_total_payable,
  setcal_foreign_bank_charges,
  setcal_your_rate,
  setCustomer_rate,
  setcal_customer_rate,
} from '../../../redux/features/CreateDeal';

export const useDebounce = (
  callback: () => void,
  delay: number,
  dependencies: any[]
) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callback();
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, dependencies);
};

export const useDealCalculation = () => {
  const [isCalcLoading, setIsCalcLoading] = useState(false);
  const [rate, setRate] = useState<number>(0);
  const [limitCustomerRate, setLimitCustomerRate] = useState(0);
  const [limitCustomerRate2, setLimitCustomerRate2] = useState(0);
  
  const dispatch = useDispatch();
  const level = localStorage.getItem("level");
  
  const {
    fx_charges,
    amount,
    currency,
    purpose_id,
    purpose_id_copy,
    type,
    created_by,
    add_on_rates,
    country,
    partner_status,
    partner_margin,
    partner_walkin_margin,
    customer_rate,
    service_partner_id
  } = useAppSelector((state) => state.CreateDeal);

  const fetchCalc = async (servicePartner?: string | number) => {
    if (!fx_charges || !amount || !currency || !purpose_id) {
      return;
    }

    setIsCalcLoading(true);

    try {
      const auth = localStorage.getItem("authToken");
      if (!auth) return;
      
      const authToken = JSON.parse(auth);
      const payload: AxiosRequestConfig<PostDealCalcRequestType> = {
        headers: { Authorization: `Bearer ${authToken}` },
        data: {
          purpose_id: purpose_id_copy === "" ? 1 : parseInt(purpose_id_copy),
          currency: currency === "" ? "USD" : currency,
          amount: parseInt(amount) || 0,
          type,
          created_by,
          tcs_type: 1,
          discount: 0,
          addon_rates: add_on_rates,
          country,
          service_partner_id: service_partner_id,
          partner_margin: partner_status === "pending" ? (partner_margin || "0") as string : "",
          partner_walkin_margin: partner_status === "pending" ? (partner_walkin_margin || "0") as string : "",
          fx_charges,
        },
      };

      if (
        currency &&
        purpose_id &&
        created_by !== "-1" &&
        (partner_status !== "pending" ||
          (partner_status === "pending" && partner_margin && partner_walkin_margin))
      ) {
        const res = await postDealCalc(payload);
        
        // Update all calculation states
        dispatch(setTcs(res?.data.tcs));
        dispatch(setServicePartnerId(res.data.service_partner));
        dispatch(setForeignBankRates(res.data.foreign_bank_charges));
        dispatch(setTTRates(res.data.tt_charges));
        dispatch(setServicePartnerMode(res.data.service_partner_mode));
        
        if (res.data.service_partner_mode === "online") {
          dispatch(setSettlementRates(res.data.settlement_rates));
        } else {
          dispatch(setSettlementRates(""));
        }
        
        dispatch(
          setcal_total_charges(
            parseFloat(res.data.payable_amount) - parseFloat(res.data.base_amount)
          )
        );
        
        // Set profit based on level
        const profitKey = `l${level}` as keyof typeof res.data.profit;
        if (res.data.profit[profitKey]) {
          dispatch(setcal_profit(res.data.profit[profitKey]));
        }
        
        dispatch(setcal_amount_inr(res.data.base_amount));
        dispatch(setcal_gst(res.data.gst));
        dispatch(setcal_tt_charges(res.data.tt_charges_with_gst));
        dispatch(setcal_total_payable(res.data.payable_amount));
        dispatch(setcal_foreign_bank_charges(res.data.foreign_bank_charges_with_gst));
        
        // Calculate cumulative commission
        let cumulativeCommission = 0;
        if (level === "2") {
          cumulativeCommission = res?.data?.cumulative_commission.l1;
        } else if (level === "3") {
          cumulativeCommission = res?.data?.cumulative_commission.l2;
        }

        const yourRate = (
          parseFloat(res?.data?.settlement_rates) + cumulativeCommission
        )?.toFixed(4);

        dispatch(setcal_your_rate(yourRate));
        
        // Calculate rate limits
        const sum_of_rates =
          parseFloat(res.data.settlement_rates) +
          res.data.cumulative_commission_without_addon.l1 +
          res.data.cumulative_commission_without_addon.l2 +
          res.data.cumulative_commission_without_addon.l3;
          
        const sum_of_rate2 =
          parseFloat(res.data.settlement_rates) +
          res.data.cumulative_commission.l1 +
          res.data.cumulative_commission.l2 +
          res.data.cumulative_commission.l3;
          
        setLimitCustomerRate(sum_of_rates);
        setLimitCustomerRate2(sum_of_rate2);
        
        setRate(
          parseFloat(res?.data?.settlement_rates) +
          (res?.data?.cumulative_commission_without_addon.l3 > 0
            ? res?.data?.cumulative_commission_without_addon.l3
            : res?.data?.cumulative_commission_without_addon.l2 > 0
              ? res?.data?.cumulative_commission_without_addon.l2
              : res?.data?.cumulative_commission_without_addon.l1)
        );
        
        dispatch(setCustomer_rate(Number(res.data.customer_rate)?.toFixed(4)));
        dispatch(setcal_customer_rate(parseFloat(res.data.customer_rate)?.toFixed(4)?.toString()));
        
        localStorage.setItem("service_partner_id", JSON.stringify(res.data.service_partner));
      }
    } catch (err: any) {
      toast.warning(err?.response?.data?.message || 'Calculation failed');
    } finally {
      setIsCalcLoading(false);
    }
  };

  // Debounced calculation
  useDebounce(
    () => {
      if (!isCalcLoading) {
        fetchCalc();
      }
    },
    1000,
    [amount, created_by, fx_charges, customer_rate, currency, purpose_id, purpose_id_copy, partner_margin, partner_walkin_margin, add_on_rates, service_partner_id]
  );

  return {
    isCalcLoading,
    rate,
    limitCustomerRate,
    limitCustomerRate2,
    fetchCalc,
  };
};